import { useVirtualizer } from "@tanstack/react-virtual";
import { last, noop } from "lodash";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { setCurrentPage } from "slices/documentViewer/documentViewerSlice";

const smoothScrollThreshold = 10;

export const getNextAnimationFrame = (onFrameId = noop) =>
  new Promise((resolve) => {
    const frameId = requestAnimationFrame(resolve);
    onFrameId(frameId);
  });

const useDocumentVirtualizer = ({
  count,
  estimateSize,
  overscan,
  scrollRef,
}) => {
  const virtualizer = useVirtualizer({
    count,
    estimateSize,
    getScrollElement: () => scrollRef.current,
    overscan,
  });
  const dispatch = useDispatch();
  const pendingScrollRef = useRef(null);
  const scrollGenerationRef = useRef(0);

  const nextFrame = async () => {
    await getNextAnimationFrame((frameId) => {
      pendingScrollRef.current = frameId;
    });

    pendingScrollRef.current = null;
  };

  const cancelPendingScroll = () => {
    scrollGenerationRef.current += 1;

    if (pendingScrollRef.current) {
      cancelAnimationFrame(pendingScrollRef.current);
      pendingScrollRef.current = null;
    }
  };

  const scrollToCenter = async (index, options = {}) => {
    dispatch(setCurrentPage(Math.max(1, index + 1)));

    const align = options.align || "center";
    const behavior = options.behavior || "smooth";
    const threshold = options.threshold ?? smoothScrollThreshold;

    cancelPendingScroll();
    const generation = scrollGenerationRef.current;

    if (!virtualizer) return;

    const virtualItems = virtualizer.getVirtualItems();
    const visibleIndices = virtualItems.map((i) => i.index);
    const firstVisible = visibleIndices[0];
    const lastVisible = last(visibleIndices);

    const isBelow = index > lastVisible;
    const distance = isBelow ? index - lastVisible : firstVisible - index;

    const shouldStage =
      behavior === "smooth" &&
      visibleIndices.length > 0 &&
      threshold > 0 &&
      distance > threshold;

    if (!shouldStage) {
      virtualizer.scrollToIndex(index, { align, behavior });
      return;
    }

    // Jump instantly to `threshold` rows short, then animate the last stretch.
    const rowCount = virtualizer.options.count;
    const stagingIndex = Math.min(
      Math.max(index + (isBelow ? -threshold : threshold), 0),
      Math.max(rowCount - 1, 0)
    );

    virtualizer.scrollToIndex(stagingIndex, {
      align,
      behavior: "auto",
    });

    await nextFrame();
    await nextFrame();

    if (generation !== scrollGenerationRef.current) return;

    virtualizer.scrollToIndex(index, { align, behavior: "smooth" });
  };

  return {
    scrollToCenter,
    virtualizer,
  };
};

export default useDocumentVirtualizer;
